export const TRACKING_LOCATIONS = {
  HEADER: 'header',
  MOBILE_MENU: 'mobileMenu',
  FOOTER: 'footer',
  HERO: 'hero',
  CTA: 'cta',
  PAGE_CONTENT: 'pageContent',
  LANDING_PAGE: 'landing_page',
  MODAL: 'modal',
} as const

export type TrackingLocationValue = (typeof TRACKING_LOCATIONS)[keyof typeof TRACKING_LOCATIONS]

export type TrackingLocation = {
  location: TrackingLocationValue
  section?: string
}

export type ElementClickEvent = {
  element_type: 'button' | 'link'
  element_text: string
  element_url?: string
  link_domain?: string
  outbound?: boolean
  link_type?: 'internal' | 'external' | 'mailto' | 'tel' | 'anchor'
  component_location: string
  section_name?: string
  page_path?: string
}

export function trackElementClick(data: ElementClickEvent): void {
  if (typeof window === 'undefined' || !window.dataLayer) {
    return
  }

  const pagePath = window.location.pathname

  const eventData = {
    event: 'element_click',
    page_path: pagePath,
    ...data,
  }

  window.dataLayer.push(eventData)

  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    console.log('GA4 Click Event:', data)
  }
}

export function isExternalUrl(href: string): boolean {
  try {
    const url = new URL(href, window.location.origin)
    return url.hostname !== window.location.hostname
  } catch {
    return false
  }
}

export function getLinkType(href: string): 'internal' | 'external' | 'mailto' | 'tel' | 'anchor' {
  if (href.startsWith('#')) return 'anchor'
  if (href.startsWith('mailto:')) return 'mailto'
  if (href.startsWith('tel:')) return 'tel'
  if (isExternalUrl(href)) return 'external'
  return 'internal'
}

export function getDomain(href: string): string | undefined {
  try {
    const url = new URL(href, window.location.origin)
    return url.hostname
  } catch {
    return undefined
  }
}

export function buildTrackingData(
  elementType: 'button' | 'link',
  text: string,
  trackingLocation: TrackingLocation,
  href?: string,
): ElementClickEvent {
  const baseData: ElementClickEvent = {
    element_type: elementType,
    element_text: text,
    component_location: trackingLocation.location,
    section_name: trackingLocation.section,
  }

  if (href) {
    const linkType = getLinkType(href)
    const isExternal = linkType === 'external'

    return {
      ...baseData,
      element_url: href,
      link_type: linkType,
      outbound: isExternal,
      link_domain: isExternal ? getDomain(href) : undefined,
    }
  }

  return baseData
}

export function dataLayerPush(eventName: string): void {
  if (typeof window === 'undefined' || !window.dataLayer) {
    return
  }

  window.dataLayer.push({ event: eventName })
}
